/* eslint-disable react/no-unstable-nested-components */
import { Table } from "@jhool-io/fe-components";
import { ColumnDef } from "@tanstack/react-table";
import {
    cn,
    formatDate,
    showMoneyInAppFormat,
} from "../../../../utils/helpers";
import { IStatementLineItem } from "../../types/statement-of-account.types";
import "./AccountStatementList.css";

interface AccountStatementListProp {
    statementBreakdownData: IStatementLineItem[];
}

interface StatementPayment {
    key: string;
    amount: number;
    date: string | null;
}

interface GroupedStatementRow {
    note_id: string;
    date_of_service: string;
    provider_name: string;
    coinsurance_amount_charged: number;
    coins_amount_outstanding: number;
    pr100_outstanding_amount: number;
    coinsPayments: StatementPayment[];
    pr100Payments: StatementPayment[];
}

function groupByNote(
    statementBreakdownData: IStatementLineItem[]
): GroupedStatementRow[] {
    const groups = new Map<string, GroupedStatementRow>();
    statementBreakdownData.forEach((item, index) => {
        const existing = groups.get(item.note_id);
        const row = existing ?? {
            note_id: item.note_id,
            date_of_service: item.date_of_service,
            provider_name:
                `${item.provider_first_name ?? ""} ${item.provider_last_name ?? ""}`.trim(),
            coinsurance_amount_charged: item.coinsurance_amount_charged,
            coins_amount_outstanding: item.coins_amount_outstanding,
            pr100_outstanding_amount: item.pr100_outstanding_amount,
            coinsPayments: [],
            pr100Payments: [],
        };
        const coinsPaid = Number(item.coins_paid_amount ?? 0);
        if (coinsPaid > 0) {
            row.coinsPayments.push({
                key: `${item.note_id}-ci-${index}`,
                amount: coinsPaid,
                date: item.coins_paid_date ?? null,
            });
        }
        const pr100Paid = Number(item.pr100_payment_amount ?? 0);
        if (pr100Paid > 0) {
            row.pr100Payments.push({
                key: `${item.note_id}-pr100-${index}`,
                amount: pr100Paid,
                date: item.pr100_payment_date ?? null,
            });
        }
        if (!existing) {
            groups.set(item.note_id, row);
        }
    });
    return Array.from(groups.values());
}

function renderPayments(payments: StatementPayment[]) {
    if (payments.length === 0) {
        return "--";
    }
    return (
        <div>
            {payments.map((payment) => (
                <div
                    key={payment.key}
                    className="[&:not(:last-child)]:border-b [&:not(:last-child)]:border-b-strokedark"
                >
                    {showMoneyInAppFormat(payment.amount)}
                    <p className="text-xs text-secondary-sub">
                        {payment.date ? formatDate(payment.date) : "--"}
                    </p>
                </div>
            ))}
        </div>
    );
}

export default function AccountStatementList({
    statementBreakdownData,
}: AccountStatementListProp) {
    const rows = groupByNote(statementBreakdownData);

    const columns: ColumnDef<GroupedStatementRow>[] = [
        {
            accessorKey: "date_of_service",
            header: "DATE OF SERVICE",
            cell: ({ row }) => formatDate(row.original.date_of_service),
        },
        {
            accessorKey: "provider_name",
            header: "PROVIDER",
            cell: ({ row }) => row.original.provider_name || "--",
        },
        {
            accessorKey: "coinsurance_amount_charged",
            header: "Charge",
            cell: ({ row }) =>
                showMoneyInAppFormat(row.original.coinsurance_amount_charged),
        },
        {
            accessorKey: "coinsurance_paid",
            header: "Session Fee Paid",
            cell: ({ row }) => renderPayments(row.original.coinsPayments),
        },
        {
            accessorKey: "pr100_collected_amount",
            header: "Insurance Reimbursement",
            cell: ({ row }) => renderPayments(row.original.pr100Payments),
        },
        {
            accessorKey: "balance",
            header: "Balance",
            cell: ({ row }) => {
                const sum =
                    row.original.coins_amount_outstanding +
                    row.original.pr100_outstanding_amount;

                const isCredit = sum < 0;
                const displayAmount = showMoneyInAppFormat(Math.abs(sum));

                return (
                    <span className={cn({ "text-danger": isCredit })}>
                        {isCredit ? `(${displayAmount})` : displayAmount}
                    </span>
                );
            },
        },
    ];

    return <Table data={rows} columns={columns} className="statementslist" />;
}
